import { ControlPlaneError } from "../api";
import { APP_LOCALE } from "../locale";

/**
 * Read-only projections of the `/v1/knowledge/list_source_items` and
 * `/v1/knowledge/fetch` envelopes used by the collected-data library.
 *
 * apps/web deliberately does not depend on `@obsina/query`, so every consumed
 * union is restated here and re-validated on the wire, exactly as
 * `commitments.ts` does for the commitment surface. An unrecognized value fails
 * closed rather than being coerced: this surface exists to say truthfully what
 * Obsina holds, and a coerced status is the one thing that would break it.
 */

export type KnowledgeAnswerStatus = "ok" | "unknown";
export type KnowledgeUnknownReason =
  "coverage_unknown" | "no_authorized_evidence" | "document_incomplete";
export type KnowledgeCoverageState = "complete" | "partial" | "unknown";
/**
 * Whether anything behind a shortfall can still move. It is what separates
 * "the sources are still catching up" from "this is as far as it will ever be
 * read", and the surface must not offer the first when the envelope says the
 * second.
 */
export type KnowledgeCoverageDurability =
  "settled" | "transient" | "terminal" | "mixed";
export type SourceLayerKind = "canonical_native" | "ocr_derived";
export type VisibilityClass = "tenant_shared" | "owner_private";

/** Mirrors `app_private.extraction_status`; see the query package contract. */
export const TEXT_STATUSES = [
  "pending",
  "success",
  "partial",
  "unsupported",
  "failed",
] as const;
export type TextStatus = (typeof TEXT_STATUSES)[number];

/** Mirrors `app_private.source_lifecycle_status`. */
export const LIFECYCLE_STATUSES = [
  "active",
  "deleted",
  "inaccessible",
] as const;
export type LifecycleStatus = (typeof LIFECYCLE_STATUSES)[number];

/**
 * What the `Text` column may say, which is not the same question as
 * {@link TextStatus}.
 *
 * `textStatus` is the verdict of a reading that already happened. `withdrawn`
 * is a statement about *now*: the item was read in full and the document that
 * reading produced is no longer published, so there is nothing to open. Both
 * are true at once, and they are not interchangeable — printing the first
 * where the second applies is what made the list say "Read in full" over an
 * item whose own dialog said "Text not published".
 */
export type ShownTextState = TextStatus | "withdrawn";

/** Mirrors `app_private.source_kind`. */
export const SOURCE_KINDS = [
  "email_message",
  "file_attachment",
  "item_attachment",
  "reference_attachment",
  "inline_attachment",
  "document",
  "calendar_event",
  "teams_message",
  "whatsapp_message",
  "drive_item",
] as const;
export type SourceKind = (typeof SOURCE_KINDS)[number];

/**
 * Mirrors the query package's closed `SourceUnreadableReason`. Restated here
 * rather than imported for the reason at the top of this file, and validated on
 * the wire: an unrecognized value becomes null, so a code nobody taught this
 * surface produces no sentence instead of a wrong one.
 */
/**
 * Mirrors the query package's closed `SourcePartialReason`, restated here for
 * the reason at the top of this file and validated on the wire.
 *
 * These are the pipeline's own four buckets, not a wording this surface
 * invented: `media_partial_reason_heads` derives them from the newest live
 * reading. `unattributed` is kept apart from an absent value because they are
 * different facts — one says the reading explains nothing, the other says there
 * is no reading to explain anything — and a reader is owed the difference.
 */
export const PARTIAL_REASONS = [
  "low_confidence",
  "no_meaningful_text",
  "rejected",
  "unattributed",
] as const;
export type PartialReason = (typeof PARTIAL_REASONS)[number];

export const UNREADABLE_REASONS = [
  "no_reader_for_format",
  "reading_failed",
] as const;
export type UnreadableReason = (typeof UNREADABLE_REASONS)[number];

/**
 * The coarse channel a row is labelled with. Several source kinds share one
 * channel — the four attachment kinds differ by how the provider delivered the
 * bytes, which is not a distinction a reader browsing their own data cares
 * about — so the filter offers channels and sends the kinds behind them.
 */
export type SourceChannel =
  | "mail"
  | "attachment"
  | "document"
  | "drive"
  | "teams"
  | "calendar"
  | "whatsapp";

const CHANNEL_KINDS: Readonly<Record<SourceChannel, readonly SourceKind[]>> = {
  mail: ["email_message"],
  attachment: [
    "file_attachment",
    "item_attachment",
    "reference_attachment",
    "inline_attachment",
  ],
  document: ["document"],
  drive: ["drive_item"],
  teams: ["teams_message"],
  calendar: ["calendar_event"],
  whatsapp: ["whatsapp_message"],
};

export function kindsForChannel(channel: SourceChannel): readonly SourceKind[] {
  return CHANNEL_KINDS[channel];
}

const KIND_CHANNEL = new Map<SourceKind, SourceChannel>(
  (
    Object.entries(CHANNEL_KINDS) as [SourceChannel, readonly SourceKind[]][]
  ).flatMap(([channel, kinds]) =>
    kinds.map((kind): [SourceKind, SourceChannel] => [kind, channel]),
  ),
);

export function channelOfKind(kind: SourceKind): SourceChannel {
  // Every member of SOURCE_KINDS is mapped above, and the wire value is
  // validated against that same list before this is called.
  return KIND_CHANNEL.get(kind) ?? "document";
}

export interface SourceItem {
  readonly sourceItemId: string;
  readonly sourceKind: SourceKind;
  readonly channel: SourceChannel;
  /** Null when the channel records no name; never a provider identifier. */
  readonly title: string | null;
  readonly sourceUrl: string | null;
  readonly parentSourceItemId: string | null;
  readonly occurredAt: string;
  readonly textStatus: TextStatus;
  /**
   * Whether the provider still offers the item. It is what separates the two
   * ways a read item can end up with nothing to open: the source is gone, or
   * the source moved on and the reading was not re-published. A member can act
   * on neither, but they are owed the difference.
   */
  readonly lifecycleStatus: LifecycleStatus;
  readonly documentVersionId: string | null;
  readonly layerKind: SourceLayerKind | null;
  readonly characterCount: number | null;
  readonly byteSize: number | null;
  readonly mediaType: string | null;
  /** Why there is no text, when the pipeline classified it; null when it did not. */
  readonly unreadableReason: UnreadableReason | null;
  /**
   * Why a `partial` reading stopped short; null when no reading stands behind
   * the item at all. Released by the API only beside `textStatus: "partial"`.
   */
  readonly partialReason: PartialReason | null;
  readonly visibility: VisibilityClass;
}

export interface SourceCoverage {
  readonly state: KnowledgeCoverageState;
  readonly asOf: string;
  readonly requiredSourceCount: number;
  readonly reconciledSourceCount: number;
  /** Null when the envelope names none; never guessed. */
  readonly durability: KnowledgeCoverageDurability | null;
}

export interface SourceItemsResult {
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  readonly items: readonly SourceItem[];
  readonly coverage: SourceCoverage;
  readonly nextCursor: string | null;
}

export interface SourceBlock {
  readonly documentBlockId: string;
  readonly sequenceNumber: number;
  readonly markdown: string;
  readonly layerKind: SourceLayerKind | null;
}

export interface SourceTextResult {
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  /**
   * Carried from the same envelope as the blocks, so a withheld text can say
   * whether waiting is worth anything without the dialog having to ask the list
   * what it knows.
   */
  readonly durability: KnowledgeCoverageDurability | null;
  readonly title: string | null;
  readonly canonicalUrl: string | null;
  readonly totalBlockCount: number;
  readonly layerKind: SourceLayerKind | null;
  readonly blocks: readonly SourceBlock[];
  readonly nextCursor: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function invalid(): never {
  throw new ControlPlaneError(
    "The knowledge surface returned an invalid response.",
  );
}

function requireString(value: unknown): string {
  if (typeof value !== "string") invalid();
  return value;
}

function nullableString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") invalid();
  return value;
}

function requireTimestamp(value: unknown): string {
  const timestamp = requireString(value);
  if (!Number.isFinite(Date.parse(timestamp))) invalid();
  return timestamp;
}

function nullableCount(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    invalid();
  }
  return value;
}

function nonNegativeInteger(value: unknown): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    invalid();
  }
  return value;
}

function answerStatus(value: unknown): KnowledgeAnswerStatus {
  if (value === "ok" || value === "unknown") return value;
  return invalid();
}

function unknownReason(value: unknown): KnowledgeUnknownReason | null {
  if (value === null || value === undefined) return null;
  if (
    value === "coverage_unknown" ||
    value === "no_authorized_evidence" ||
    value === "document_incomplete"
  ) {
    return value;
  }
  return invalid();
}

/**
 * Additive, and unrecognized-means-null: durability only ever decides which of
 * two sentences to print, so a value this build does not know must produce the
 * sentence that promises nothing, never a guess in either direction.
 */
function coverageDurability(
  value: unknown,
): KnowledgeCoverageDurability | null {
  return value === "settled" ||
    value === "transient" ||
    value === "terminal" ||
    value === "mixed"
    ? value
    : null;
}

function coverageState(value: unknown): KnowledgeCoverageState {
  if (value === "complete" || value === "partial" || value === "unknown") {
    return value;
  }
  return invalid();
}

function visibilityClass(value: unknown): VisibilityClass {
  if (value === "tenant_shared" || value === "owner_private") return value;
  return invalid();
}

function textStatus(value: unknown): TextStatus {
  if ((TEXT_STATUSES as readonly string[]).includes(requireString(value))) {
    return value as TextStatus;
  }
  return invalid();
}

function lifecycleStatus(value: unknown): LifecycleStatus {
  if (
    (LIFECYCLE_STATUSES as readonly string[]).includes(requireString(value))
  ) {
    return value as LifecycleStatus;
  }
  return invalid();
}

function sourceKind(value: unknown): SourceKind {
  if ((SOURCE_KINDS as readonly string[]).includes(requireString(value))) {
    return value as SourceKind;
  }
  return invalid();
}

/**
 * The API's additive `layerKind`. A missing or unrecognized value is null, which
 * the surface renders as "unlabeled" — never as canonical, because presenting an
 * OCR reading as the provider's own words is the one mislabel that matters here.
 */
function layerKind(value: unknown): SourceLayerKind | null {
  return value === "canonical_native" || value === "ocr_derived" ? value : null;
}

function parseCoverage(value: unknown): SourceCoverage {
  if (!isRecord(value)) invalid();
  return {
    state: coverageState(value.state),
    asOf: requireTimestamp(value.asOf),
    requiredSourceCount: nonNegativeInteger(value.requiredSourceCount),
    reconciledSourceCount: nonNegativeInteger(value.reconciledSourceCount),
    durability: coverageDurability(value.durability),
  };
}

/**
 * Additive, and unrecognized-means-null on purpose: this value is only ever
 * turned into a sentence, so the safe reading of a code this build does not know
 * is "no reason was recorded", never the code itself.
 */
function unreadableReason(value: unknown): UnreadableReason | null {
  return (UNREADABLE_REASONS as readonly unknown[]).includes(value)
    ? (value as UnreadableReason)
    : null;
}

/** Additive and unrecognized-means-null, on the same terms as the reason above. */
function partialReason(value: unknown): PartialReason | null {
  return (PARTIAL_REASONS as readonly unknown[]).includes(value)
    ? (value as PartialReason)
    : null;
}

function parseItem(value: unknown): SourceItem {
  if (!isRecord(value)) invalid();
  const kind = sourceKind(value.sourceKind);
  const documentVersionId = nullableString(value.documentVersionId);
  return {
    sourceItemId: requireString(value.sourceItemId),
    sourceKind: kind,
    channel: channelOfKind(kind),
    title: nullableString(value.title),
    sourceUrl: nullableString(value.sourceUrl),
    parentSourceItemId: nullableString(value.parentSourceItemId),
    occurredAt: requireTimestamp(value.occurredAt),
    textStatus: textStatus(value.textStatus),
    lifecycleStatus: lifecycleStatus(value.lifecycleStatus),
    documentVersionId,
    layerKind: layerKind(value.layerKind),
    characterCount: nullableCount(value.characterCount),
    byteSize: nullableCount(value.byteSize),
    mediaType: nullableString(value.mediaType),
    unreadableReason: unreadableReason(value.unreadableReason),
    partialReason: partialReason(value.partialReason),
    visibility: visibilityClass(value.visibility),
  };
}

export function parseSourceItemsResponse(value: unknown): SourceItemsResult {
  if (!isRecord(value)) invalid();
  const status = answerStatus(value.status);
  const coverage = parseCoverage(value.coverage);
  const data = value.data;
  if (status === "unknown") {
    // An unknown answer carries no data at all. Reading items out of one would
    // be exactly the "empty result that reads as no such record" the contract
    // forbids, in reverse.
    return {
      status,
      reason: unknownReason(value.reason),
      items: [],
      coverage,
      nextCursor: null,
    };
  }
  if (!isRecord(data) || !Array.isArray(data.items)) invalid();
  return {
    status,
    reason: unknownReason(value.reason),
    items: data.items.map(parseItem),
    coverage,
    nextCursor: nullableString(value.nextCursor),
  };
}

function parseBlock(value: unknown): SourceBlock {
  if (!isRecord(value)) invalid();
  return {
    documentBlockId: requireString(value.documentBlockId),
    sequenceNumber: nonNegativeInteger(value.sequenceNumber),
    markdown: requireString(value.markdown),
    layerKind: layerKind(value.layerKind),
  };
}

export function parseSourceTextResponse(value: unknown): SourceTextResult {
  if (!isRecord(value)) invalid();
  const status = answerStatus(value.status);
  const data = value.data;
  const durability = isRecord(value.coverage)
    ? coverageDurability(value.coverage.durability)
    : null;
  if (status === "unknown") {
    return {
      status,
      reason: unknownReason(value.reason),
      durability,
      title: null,
      canonicalUrl: null,
      totalBlockCount: 0,
      layerKind: null,
      blocks: [],
      nextCursor: null,
    };
  }
  if (!isRecord(data) || !Array.isArray(data.blocks)) invalid();
  return {
    status,
    reason: unknownReason(value.reason),
    durability,
    title: nullableString(data.title),
    canonicalUrl: nullableString(data.canonicalUrl),
    totalBlockCount: nonNegativeInteger(data.totalBlockCount),
    layerKind: layerKind(data.layerKind),
    blocks: data.blocks.map(parseBlock),
    nextCursor: nullableString(value.nextCursor),
  };
}

/**
 * What the surface may say about an item's text — the one function the list
 * badge and the dialog both read, so the two can no longer disagree.
 *
 * The disagreement they used to have was not cosmetic. `textStatus` is the
 * verdict of a reading that already happened, and it stays `success` forever
 * once a reading landed. `documentVersionId` is whether there is a published
 * document to open *now*. Measured on the live tenant on 2026-07-29, 48 items
 * had both `success` and no publication: the list called them "Read in full"
 * and their own dialog called them "Text not published", about the same row.
 *
 * `withdrawn` is the missing word. It says the reading happened and its
 * document is gone, which is the whole truth and is what {@link withdrawalKind}
 * then explains. Every other status already agrees with the absence it
 * describes, so it passes through untouched.
 */
export function shownTextState(item: SourceItem): ShownTextState {
  if (item.documentVersionId !== null) return item.textStatus;
  return item.textStatus === "success" ? "withdrawn" : item.textStatus;
}

/**
 * Which of the two ways a read item lost its text. Both are outside a member's
 * control, and neither resolves by waiting, but they are different facts and
 * the second one is not the member's doing either — so the surface says which.
 *
 * `removed` — the provider no longer offers the item (they deleted the mail).
 * `stale` — the item is still there, and the record it was read against moved
 * on without the reading being re-published.
 */
export function withdrawalKind(item: SourceItem): "removed" | "stale" {
  return item.lifecycleStatus === "active" ? "stale" : "removed";
}

/**
 * Byte sizes in the units a reader reads, and characters as characters. Both
 * return null for null so a row with no measure renders nothing rather than a
 * confident "0".
 */
export function formatByteSize(bytes: number | null): string | null {
  if (bytes === null) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ["kB", "MB", "GB", "TB"] as const;
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/**
 * The provider's media type as a word a reader recognizes, or null when it
 * carries nothing worth saying.
 *
 * Null for `application/octet-stream` specifically: that value is not a
 * provider's declaration but the filler three write paths apply when the
 * provider declared nothing, so printing it would state a format nobody
 * established. Null for a long or unfamiliar subtype for the plainer reason
 * that `VND.OPENXMLFORMATS-OFFICEDOCUMENT...` is not a word.
 */
export function mediaFormatLabel(mediaType: string | null): string | null {
  if (mediaType === null) return null;
  const [type, rest] = mediaType.toLowerCase().split("/", 2);
  const subtype = (rest ?? "").split(";", 1)[0]?.trim() ?? "";
  if (subtype === "" || subtype === "octet-stream") return null;
  const word = /^[a-z0-9+-]{1,8}$/.test(subtype) ? subtype.toUpperCase() : null;
  if (type === "image") return word === null ? "image" : `${word} image`;
  return word;
}

/**
 * What to call a row the provider never named.
 *
 * Never the item's id. The read surface deliberately answers null rather than
 * an identifier — see `SourceItemRecord.title` — and this is the other half of
 * that promise: the label says what the item is, out of what is actually known
 * about it, and says nothing more.
 */
export function untitledLabel(
  item: SourceItem,
  messages: {
    readonly untitled: string;
    readonly untitledMessage: string;
    readonly untitledFormat: (format: string) => string;
  },
): string {
  const format = mediaFormatLabel(item.mediaType);
  if (format !== null) return messages.untitledFormat(format);
  // The channels whose items are messages: what is missing there is a subject,
  // and calling it a name would misdescribe the thing that is absent.
  if (
    item.channel === "mail" ||
    item.channel === "teams" ||
    item.channel === "whatsapp"
  ) {
    return messages.untitledMessage;
  }
  return messages.untitled;
}

export function formatItemDate(iso: string): string {
  const parsed = new Date(iso);
  if (!Number.isFinite(parsed.getTime())) return iso;
  return parsed.toLocaleDateString(APP_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatAsOf(iso: string): string {
  const parsed = new Date(iso);
  if (!Number.isFinite(parsed.getTime())) return iso;
  return parsed.toLocaleString(APP_LOCALE, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * One row of the list as it is drawn: an item, and the items this list holds
 * that were delivered inside it.
 */
export interface InventoryGroup {
  readonly item: SourceItem;
  /** Loaded children of {@link item}, in the order the server returned them. */
  readonly children: readonly SourceItem[];
}

/**
 * Folds attachments under the item they arrived in.
 *
 * A single mail with six images produced six rows reading "Adsız PNG image",
 * scattered wherever the keyset put them — the list said what Obsina holds and
 * nothing about how any of it belongs together. The link is the record's own
 * `parentSourceItemId`; nothing is inferred from names, times or types.
 *
 * Three rules keep this a presentation and not a claim:
 *
 * - **Nothing is dropped and nothing moves twice.** Every input item comes out
 *   exactly once, either as a group's item or inside one group's children.
 * - **A parent this page has not loaded is not a parent.** The list is paged, so
 *   an attachment can arrive before the mail it belongs to (or under a filter
 *   that excludes mail entirely). Such a row stays where it was, at the top
 *   level, rather than being hidden under a row that is not on screen.
 * - **The nesting is one level deep.** An attachment inside an attachment is
 *   folded under the topmost ancestor this page holds, so a reader never has to
 *   open two rows to reach one file. The walk carries a visited set, so a
 *   parent chain that loops resolves to itself instead of hanging.
 *
 * The count a group states is therefore "the attachments of this item that are
 * in this list", which is what the caller must say — a later page may add more.
 */
export function groupByParent(
  items: readonly SourceItem[],
): readonly InventoryGroup[] {
  const byId = new Map(items.map((item) => [item.sourceItemId, item] as const));
  const topAncestor = (item: SourceItem): SourceItem => {
    const seen = new Set<string>([item.sourceItemId]);
    let current = item;
    for (;;) {
      const parentId = current.parentSourceItemId;
      if (parentId === null || seen.has(parentId)) return current;
      const parent = byId.get(parentId);
      if (parent === undefined) return current;
      seen.add(parentId);
      current = parent;
    }
  };

  // Resolved once for every row, so the second pass can both place a child and
  // recognise a parent without walking the chain twice.
  const ancestorOf = new Map(
    items.map((item) => [item.sourceItemId, topAncestor(item)] as const),
  );
  // A row may only fold under one that folds under nothing. Without this a pair
  // whose parents point at each other resolved to each other, both counted as
  // children, and the list drew neither of them — a loop in the records must
  // cost a fold, never a row.
  const isRoot = (item: SourceItem): boolean =>
    ancestorOf.get(item.sourceItemId)?.sourceItemId === item.sourceItemId;
  const parentOf = (item: SourceItem): SourceItem | null => {
    const ancestor = ancestorOf.get(item.sourceItemId);
    if (!ancestor || ancestor.sourceItemId === item.sourceItemId) return null;
    return isRoot(ancestor) ? ancestor : null;
  };

  const children = new Map<string, SourceItem[]>();
  for (const item of items) {
    const parent = parentOf(item);
    if (parent === null) continue;
    const bucket = children.get(parent.sourceItemId) ?? [];
    bucket.push(item);
    children.set(parent.sourceItemId, bucket);
  }
  return items
    .filter((item) => parentOf(item) === null)
    .map((item) => ({
      item,
      children: children.get(item.sourceItemId) ?? [],
    }));
}
